import { wheelApiService } from '../../services/wheelApi';
import { promotionApiService, Promotion } from '../../services/promotionApi';
import { serviceApiService, Service } from '../../services/serviceApi';

export interface WheelSegment {
  id: string;
  label: string;
  color: string;
  rotation: number;
  promotion?: Promotion;
}

export interface WheelConfig {
  can_play: boolean;
  can_win: boolean;
  days_until_next_play: number;
  days_until_next_win: number;
  replay_delay_days: number;
  win_delay_days: number;
}

export interface WheelData {
  active: boolean;
  config: WheelConfig;
  items: any[];
}

export class MobilePromoWheelConfig {
  // Couleurs des segments (identiques au desktop)
  static readonly SEGMENT_COLORS = ['#FFD700', '#1E90FF', '#FFFFFF', '#FFA500', '#4169E1', '#F0E68C', '#87CEEB', '#FFE4B5'];
  
  // Configuration de l'animation de rotation
  static readonly SPIN_CONFIG = {
    baseRotation: 360 * 5,
    duration: 3000, // 3 secondes
    transition: 'transform 5s cubic-bezier(0.17, 0.67, 0.12, 0.99)'
  };

  // Messages et textes
  static readonly MESSAGES = {
    welcomeGuest: {
      title: '👋 Bienvenue!',
      subtitle: 'Pour participer, connectez-vous.',
      hint: '💡 Pas de compte? Inscrivez-vous!'
    },
    waitingToPlay: {
      title: '⏰ Patience!',
      subtitle: 'Revenez dans'
    },
    readyToPlay: {
      title: '🎉 C\'est parti!',
      subtitle: 'Cliquez sur le bouton pour tourner:'
    },
    activePromotions: 'Vous avez déjà des promotions actives ! Réclamez-les d\'abord avant de jouer à nouveau.',
    loginRequired: 'Vous devez vous connecter pour jouer à la roue de promo !',
    congratulations: 'Félicitations!',
    notThisTime: 'Pas cette fois!',
    retryIn: 'Retentez votre chance dans'
  };

  // Boutons et actions
  static readonly BUTTONS = {
    spinning: '⏳ Tourne...',
    login: '🔒 Connexion',
    activePromos: '🎯 Promotions actives',
    waiting: '⏰',
    spin: '🎰 TOURNER LA ROUE',
    claim: 'Réclamez ici',
    close: 'Fermer',
    connect: 'Se connecter',
    maybeLater: 'Peut-être plus tard',
    register: 'Inscrivez-vous gratuitement'
  };

  // Règles du jeu
  static readonly GAME_RULES = {
    howToPlay: [
      '• Connectez-vous',
      '• Cliquez sur "TOURNER"',
      '• Attendez le résultat',
      '• Code promo auto'
    ],
    conditions: [
      '• Selon conditions promo',
      '• Non cumulable',
      '• Services éligibles'
    ]
  };

  // Fonction pour formater le délai en format lisible (identique au desktop)
  static formatDelay(days: number): string {
    const totalMinutes = Math.ceil(days * 24 * 60);
    const d = Math.floor(totalMinutes / (24 * 60));
    const h = Math.floor((totalMinutes % (24 * 60)) / 60);
    const m = totalMinutes % 60;
    
    if (d > 0) {
      return `${d} jour${d > 1 ? 's' : ''}${h > 0 ? ` ${h}h` : ''}${m > 0 ? ` ${m}min` : ''}`;
    } else if (h > 0) {
      return `${h} heure${h > 1 ? 's' : ''}${m > 0 ? ` ${m}min` : ''}`;
    } else {
      return `${m} minute${m > 1 ? 's' : ''}`;
    }
  }

  // Fonction pour obtenir le nom d'un service par son ID (identique au desktop)
  static getServiceName(serviceId: string | undefined, services: Service[]): string {
    if (!serviceId) return 'Service non défini';
    const service = services.find(s => s.id === serviceId);
    return service ? service.name : `Service #${serviceId}`;
  }

  // Chargement des données de la roue (identique au desktop)
  static async loadWheelData(isAuthenticated: boolean): Promise<{
    data: WheelData;
    services: Service[];
    userActivePromotions: Promotion[];
  }> {
    try {
      // Charger les données de la roue et les services en parallèle
      const [data, servicesData] = await Promise.all([
        wheelApiService.getWheelData(),
        serviceApiService.getServices()
      ]);
      
      // Stocker les services
      const services = Array.isArray(servicesData) ? servicesData : [];
      
      let userActivePromotions: Promotion[] = [];
      
      // Si l'utilisateur est authentifié, charger aussi ses promotions actives
      if (isAuthenticated) {
        try {
          const userPromoCodesData = await promotionApiService.getUserPromoCodes();
          userActivePromotions = Array.isArray(userPromoCodesData) ? userPromoCodesData : [];
        } catch (error) {
          console.log('Impossible de charger les promotions utilisateur:', error);
          userActivePromotions = [];
        }
      }
      
      return { data, services, userActivePromotions };
    } catch (error) {
      console.error('Erreur chargement roue:', error);
      throw error;
    }
  }

  // Création des segments de la roue (identique au desktop)
  static createWheelSegments(items: any[]): WheelSegment[] {
    return items.map((item, i) => ({
      id: `segment-${item.id}`,
      label: item.label,
      color: this.SEGMENT_COLORS[i % this.SEGMENT_COLORS.length],
      rotation: (i * 360) / items.length,
      promotion: item.promotion_data as any
    }));
  }

  // Validation avant de jouer (identique au desktop)
  static validateCanPlay(
    isAuthenticated: boolean,
    userActivePromotions: Promotion[],
    canPlay: boolean,
    daysUntilNextPlay: number
  ): { canPlay: boolean; message?: string } {
    if (!isAuthenticated) {
      return { canPlay: false, message: 'login_required' };
    }

    if (userActivePromotions.length > 0) {
      return { canPlay: false, message: 'active_promotions' };
    }

    if (!canPlay) {
      return { canPlay: false, message: `wait_${this.formatDelay(daysUntilNextPlay)}` };
    }

    return { canPlay: true };
  }

  // Logique de rotation de la roue (identique au desktop)
  static calculateWheelRotation(segments: WheelSegment[], winningItemId: string): number {
    const winningIndex = segments.findIndex(s => s.id === `segment-${winningItemId}`);
    
    if (winningIndex === -1) return 0;
    
    const segmentAngle = 360 / segments.length;
    const targetAngle = 360 - (winningIndex * segmentAngle + segmentAngle / 2);
    return this.SPIN_CONFIG.baseRotation + targetAngle;
  }

  // Sauvegarde du code promo (identique au desktop)
  static async savePromoCode(
    result: any,
    isAuthenticated: boolean
  ): Promise<Promotion | null> {
    console.log('🔍 Résultat complet de la roue:', result);
    console.log('🔍 Utilisateur authentifié:', isAuthenticated);
    
    if (result.promotion && result.promotion.id) {
      console.log('🎯 Tentative de sauvegarde du code promo:', {
        promotionId: result.promotion.id,
        code: result.promotion.code,
        name: result.promotion.name,
        isAuthenticated: isAuthenticated
      });
      
      if (!isAuthenticated) {
        console.log('⚠️ Utilisateur non authentifié - sauvegarde en localStorage uniquement');
        const promoData = {
          code: result.promotion.code,
          discount: result.promotion.discount_value || 0,
          discount_type: result.promotion.discount_type,
          source: 'wheel',
          won_date: new Date().toISOString(),
          promotion_name: result.promotion.name,
          end_date: result.promotion.end_date || null
        };
        
        const existingCodes = localStorage.getItem('userPromoCodes');
        let codes = existingCodes ? JSON.parse(existingCodes) : [];
        const codeExists = codes.find((c: any) => c.code === promoData.code);
        if (!codeExists) {
          codes.push(promoData);
          localStorage.setItem('userPromoCodes', JSON.stringify(codes));
          console.log('🎁 Code promo sauvegardé en localStorage (non authentifié):', promoData);
        }
        return result.promotion;
      } else {
        try {
          // 1. Récupérer les informations complètes de la promotion
          const fullPromotion = await promotionApiService.getPromotion(result.promotion.id);
          console.log('📋 Informations complètes de la promotion:', fullPromotion);
          
          // 2. Activer la promotion si elle n'est pas déjà active
          if (fullPromotion.status !== 'active') {
            await promotionApiService.updatePromotion(result.promotion.id, { status: 'active' });
            console.log('✅ Promotion activée avec succès');
          }
          
          // 3. Sauvegarder le code promo pour l'utilisateur
          const saveResult = await promotionApiService.saveUserPromoCode(result.promotion.id, 'wheel');
          console.log('✅ Code promo sauvegardé dans le backend avec succès:', saveResult);
          console.log('🎁 Promotion sauvegardée:', fullPromotion.code);
          
          // 4. Vérifier que le code promo est bien dans la liste de l'utilisateur
          const promotionId = result.promotion?.id;
          const promotionCode = fullPromotion.code;
          if (promotionId) {
            try {
              // Attendre un peu pour laisser le temps au backend de se synchroniser
              await new Promise(resolve => setTimeout(resolve, 1000));
              
              const userPromoCodes = await promotionApiService.getUserPromoCodes();
              console.log('🔍 Vérification détaillée:');
              console.log('  - ID recherché:', promotionId);
              console.log('  - Code recherché:', promotionCode);
              console.log('  - Nombre de codes dans la liste:', userPromoCodes.length);
              console.log('  - IDs dans la liste:', userPromoCodes.map(p => p.id));
              console.log('  - Codes dans la liste:', userPromoCodes.map(p => p.code));
              
              const isCodeInListById = userPromoCodes.some(promo => promo.id === promotionId);
              const isCodeInListByCode = userPromoCodes.some(promo => promo.code === promotionCode);
              
              if (isCodeInListById || isCodeInListByCode) {
                console.log('✅ Vérification réussie: Le code promo est bien enregistré dans la liste de l\'utilisateur');
                console.log(`  - Trouvé par ID: ${isCodeInListById}`);
                console.log(`  - Trouvé par code: ${isCodeInListByCode}`);
              } else {
                console.warn('⚠️ Attention: Le code promo ne semble pas être dans la liste de l\'utilisateur');
                console.warn('  - Vérification par ID échouée');
                console.warn('  - Vérification par code échouée');
              }
              console.log('📋 Liste complète des codes promo de l\'utilisateur:', userPromoCodes);
            } catch (verifyError) {
              console.error('❌ Erreur lors de la vérification de la liste des codes promo:', verifyError);
            }
          } else {
            console.warn('⚠️ Impossible de vérifier: ID de promotion manquant');
          }
          
          return fullPromotion;
        } catch (error: any) {
          console.error('❌ Erreur lors de la sauvegarde du code promo:', error);
          console.error('📋 Détails de l\'erreur:', error);
          console.error('📋 Status de l\'erreur:', error.response?.status);
          console.error('📋 Data de l\'erreur:', error.response?.data);
          
          // En cas d'erreur backend, sauvegarder temporairement en localStorage comme fallback
          const promoData = {
            code: result.promotion.code,
            discount: result.promotion.discount_value || 0,
            discount_type: result.promotion.discount_type,
            source: 'wheel',
            won_date: new Date().toISOString(),
            promotion_name: result.promotion.name,
            end_date: result.promotion.end_date || null
          };
          
          const existingCodes = localStorage.getItem('userPromoCodes');
          let codes = existingCodes ? JSON.parse(existingCodes) : [];
          const codeExists = codes.find((c: any) => c.code === promoData.code);
          if (!codeExists) {
            codes.push(promoData);
            localStorage.setItem('userPromoCodes', JSON.stringify(codes));
            console.log('🎁 Code promo sauvegardé en fallback localStorage:', promoData);
          }
          return result.promotion;
        }
      }
    } else {
      console.log('⚠️ Aucune promotion trouvée dans le résultat ou ID manquant');
      console.log('📊 result.promotion:', result.promotion);
      console.log('📊 result.has_won:', result.has_won);
      console.log('📊 result.promo_code:', result.promo_code);
      return null;
    }
  }

  // Logique de rotation (identique au desktop)
  static async performSpin(segments: WheelSegment[]): Promise<any> {
    return await wheelApiService.spin();
  }

  // Vérification des couleurs pour le texte (identique au desktop)
  static getTextColor(segmentColor: string): { textColor: string; strokeColor: string } {
    const isLightColor = ['#FFD700', '#FFA500', '#2ECC71'].includes(segmentColor);
    return {
      textColor: isLightColor ? '#000000' : '#FFFFFF',
      strokeColor: isLightColor ? '#FFFFFF' : '#000000'
    };
  }
}
